import assert from 'assert';
import produce from 'immer';

/**
 * Typesafe way to moves a given sequence of elements in an array to a new position.
 *
 * @example
 * reorderSubsequence([0,1,2,3,4,5,6], {startIndex: 2, endIndex: 4, insertPosition: 5}) === [0,1,4,2,3,5,6]
 * reorderSubsequence([0,1,2,3,4,5,6], {startIndex: 2, endIndex: 4, insertPosition: 0}) === [2,3,0,1,4,5,6]
 * reorderSubsequence([0,1,2,3,4,5,6], {startIndex: 1, endIndex: 2, insertPosition: 7}) === [0,2,3,4,5,6,1]
 * reorderSubsequence([0,1,2,3,4,5,6], {startIndex: 1, endIndex: 5, insertPosition: 3}) === [0,1,2,3,4,5,6] // no change
 * reorderSubsequence([0,1,2,3,4,5,6], {startIndex: 1, endIndex: 2, insertPosition: 8}) // error: invalid insertPosition
 * reorderSubsequence([0,1,2,3,4,5,6], {startIndex: 2, endIndex: 2, insertPosition: 0}) // error: invalid subsequence
 *
 * @param array Array of elements. This is not mutated.
 * @param startIndex First index of sequence of elements to move, inclusive.
 * @param endIndex Last index of sequence of elements to move, exclusive.
 * @param insertPosition New index of first element of sequence in original array index-space.
 * @returns Array with items reordered.
 */
export default function reorderSubsequence<T>(
  array: Array<T>,
  {startIndex, endIndex, insertPosition}: {startIndex: number; endIndex: number; insertPosition: number}
): Array<T> {
  assert(startIndex < endIndex, `Expected startIndex to be less than endIndex (${startIndex} < ${endIndex})`);
  assert(
    startIndex >= 0 && endIndex <= array.length,
    `Expected sequence ${startIndex}..${endIndex} to be within array bounds 0..${array.length}`
  );
  assert(
    insertPosition >= 0 && insertPosition <= array.length,
    `Expected insertPosition ${insertPosition} to be within array bounds 0..${array.length}`
  );

  if (insertPosition >= startIndex && insertPosition <= endIndex) {
    return array;
  }

  return produce(array, (draftArray) => {
    const movedItems = draftArray.splice(startIndex, endIndex - startIndex);
    const to = insertPosition > startIndex ? insertPosition - movedItems.length : insertPosition;
    draftArray.splice(to, 0, ...movedItems);
  });
}
